/**
 * Tabbed collection browser for "Shop by type".
 *
 * The first tab's products are server-rendered with the page; every other tab
 * is fetched from the Section Rendering API on first open
 * (`/collections/<handle>?section_id=fandom-tile-preview`) and cached after
 * that, so a shopper flicking between types pays for each one once.
 *
 * Deliberately separate from fandom-tile-peek.js. That component toggles a
 * panel open and shut from links that must still navigate without JS; this one
 * always has exactly one tab selected and never closes. Sharing the two would
 * mean a component that is mostly branches.
 */

const PREVIEW_SECTION = 'fandom-tile-preview';

class FandomTypeTabs extends HTMLElement {
  /** @type {Map<string, string>} collection url -> rendered html */
  #cache = new Map();
  /** @type {AbortController | null} */
  #inflight = null;
  /** @type {HTMLButtonElement | null} */
  #selected = null;

  connectedCallback() {
    this.panel = this.querySelector('[data-tile-peek-body]');
    this.tabs = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);

    if (!this.panel || !this.tabs.length) return;

    // The first tab arrived rendered. Seed the cache from the DOM so returning
    // to it never costs a request.
    this.#selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.tabs[0];
    const seedUrl = this.#selected?.dataset.peekUrl;
    if (seedUrl) this.#cache.set(seedUrl, this.panel.innerHTML);

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', this.#onKeydown);
    }
  }

  disconnectedCallback() {
    this.#inflight?.abort();
  }

  /**
   * Arrow keys move between tabs, per the tablist pattern — a tab strip that
   * only responds to Tab makes every type a stop on the way down the page.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const step = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' }[event.key];
    if (step == null) return;

    event.preventDefault();
    const current = this.tabs.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    let next;
    if (step === 'first') next = 0;
    else if (step === 'last') next = this.tabs.length - 1;
    else next = (current + step + this.tabs.length) % this.tabs.length;

    this.tabs[next]?.focus();
    this.select(this.tabs[next]);
  };

  /** @param {HTMLButtonElement} tab */
  async select(tab) {
    if (!tab || tab === this.#selected) return;

    const url = tab.dataset.peekUrl;
    if (!url) return;

    for (const other of this.tabs) {
      const isSelected = other === tab;
      other.setAttribute('aria-selected', String(isSelected));
      // Roving tabindex: only the selected tab is in the tab order.
      other.tabIndex = isSelected ? 0 : -1;
    }
    this.#selected = tab;

    const cached = this.#cache.get(url);
    if (cached != null) {
      this.panel.innerHTML = cached;
      return;
    }

    this.#inflight?.abort();
    this.#inflight = new AbortController();
    this.setAttribute('data-tabs-loading', '');

    try {
      const endpoint = new URL(url, window.location.origin);
      endpoint.searchParams.set('section_id', PREVIEW_SECTION);

      const response = await fetch(endpoint, { signal: this.#inflight.signal });
      if (!response.ok) throw new Error(`Tab request failed: ${response.status}`);

      const html = await response.text();
      this.#cache.set(url, html);

      // A slow response for a tab the shopper has since moved off must not
      // overwrite the one they are looking at now.
      if (this.#selected === tab) this.panel.innerHTML = html;
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      // No half-state: send them to the collection they asked for.
      window.location.href = url;
    } finally {
      this.removeAttribute('data-tabs-loading');
    }
  }
}

if (!customElements.get('fandom-type-tabs')) {
  customElements.define('fandom-type-tabs', FandomTypeTabs);
}
